/**
 * PlatformSalesAnalytics — the admin "Sales 360". Cross-vendor overview for Lusuku
 * staff / super-admins: platform GMV + orders trend, a vendor performance
 * leaderboard (with growth %), top movers + at-risk vendors, and type / country /
 * payment breakdowns. Delivered-only revenue. Country-scoped server-side.
 *
 * Self-contained + live: opens its own socket, joins the active country's dashboard
 * room, and debounce-refetches on new-order / dashboard-update events.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import io from "socket.io-client";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from "chart.js";
import { apiFetchPlatformSalesAnalytics } from "../api/index";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";
import { getActiveCountry } from "../utilities/activeCountry.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const NAVY = "#0b1240", TEAL = "#136274";
const fmtUGX = (n) => "UGX " + Math.round(Number(n) || 0).toLocaleString();
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(v % 1_000 ? 1 : 0) + "k";
  return String(v);
};
const RANGES = [{ id: "day", label: "Daily", sub: "Last 14 days" }, { id: "week", label: "Weekly", sub: "Last 12 weeks" }, { id: "month", label: "Monthly", sub: "Last 12 months" }];
const TYPE_LABEL = { RESTAURANT: "Restaurant", RETAILER: "Retail", WHOLESALER: "Wholesale", ONLINE: "Online", HEALTHCARE: "Healthcare", BRAND_OWNER: "Brand", "3PL_PARTNER": "3PL", SELLER: "Retail", other: "Other" };
const PAY_LABEL = { COD: "Cash", MOMO: "MTN MoMo", MTN: "MTN MoMo", AIRTEL: "Airtel", CARD: "Card", VISA: "Card", OTHER: "Other" };

const growthTag = (g) => {
  if (g == null) return <span style={{ color: "#8a97ad", fontWeight: 600, fontSize: 12 }}>new</span>;
  const up = g >= 0;
  return <span style={{ color: up ? "#0f9d58" : "#d64545", fontWeight: 700 }}>{up ? "▲" : "▼"} {Math.abs(g)}%</span>;
};

const S = {
  wrap: { background: "#fff", borderRadius: 16, boxShadow: "0 6px 22px rgba(15,23,42,0.07)", padding: "22px 24px", margin: "0 0 24px" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: "1.15rem", color: "#0f172a", margin: 0 },
  toggle: { display: "inline-flex", background: "#f1f4f8", borderRadius: 10, padding: 3, gap: 2 },
  tBtn: (on) => ({ border: "none", background: on ? "#fff" : "transparent", color: on ? NAVY : "#64748b", fontWeight: 600, fontSize: 13, padding: "7px 14px", borderRadius: 8, cursor: "pointer", boxShadow: on ? "0 1px 4px rgba(15,23,42,.12)" : "none" }),
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 },
  card: (accent) => ({ background: "#f8fafc", border: "1px solid #eef1f6", borderLeft: `4px solid ${accent}`, borderRadius: 12, padding: "14px 16px" }),
  cardLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#90a0b7", marginBottom: 6 },
  cardValue: { fontSize: "1.5rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 },
  cardMeta: { fontSize: 12.5, color: "#6b7a99", marginTop: 6 },
  chartBox: { height: 240, marginBottom: 8 },
  sectionTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#90a0b7", margin: "16px 0 10px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
  th: { textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "2px solid #eef1f6", whiteSpace: "nowrap" },
  td: { padding: "9px 10px", borderBottom: "1px solid #f4f6fb", whiteSpace: "nowrap" },
  scroll: { maxHeight: 360, overflow: "auto", border: "1px solid #eef1f6", borderRadius: 12 },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 18 },
  panel: { border: "1px solid #eef1f6", borderRadius: 12, padding: "14px 16px" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid #f4f6fb", fontSize: 13.5 },
  muted: { color: "#8a97ad", fontSize: 13 },
  pill: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "#eef2fb", color: "#0b1240" },
};

export default function PlatformSalesAnalytics() {
  const [range, setRange] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) { setLoading(true); setError(null); }
    try {
      const res = await apiFetchPlatformSalesAnalytics(rangeRef.current);
      setData(res.data);
      setError(null);
    } catch (e) {
      if (!silent) setError(e?.response?.data?.error || "Couldn't load platform analytics.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, range]);

  // Live refresh on new orders / dashboard updates (active country room).
  useEffect(() => {
    const url = process.env.REACT_APP_SERVER_URL;
    if (!url) return undefined;
    const token = getTokenFromLocalStorage && getTokenFromLocalStorage();
    const socket = io(url, token ? { auth: { token } } : undefined);
    const join = () => { try { socket.emit("join", `dashboardRoom:${getActiveCountry()}`); } catch (_e) { /* ignore */ } };
    socket.on("connect", join);
    let timer = null;
    const bump = () => { clearTimeout(timer); timer = setTimeout(() => load({ silent: true }), 2000); };
    ["newOrderAlert", "dashboardUpdate"].forEach((ev) => socket.on(ev, bump));
    return () => { clearTimeout(timer); socket.off(); socket.disconnect(); };
  }, [load]);

  const chart = useMemo(() => {
    const t = data?.trend || [];
    return {
      data: { labels: t.map((x) => x.label), datasets: [{ label: "GMV", data: t.map((x) => x.gmv), backgroundColor: TEAL, hoverBackgroundColor: NAVY, borderRadius: 6, maxBarThickness: 34 }] },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { title: (i) => i[0]?.label, label: (ctx) => `${fmtUGX(ctx.parsed.y)} · ${t[ctx.dataIndex]?.orders || 0} order(s)` } } },
        scales: { y: { beginAtZero: true, ticks: { callback: (v) => fmtShort(v), color: "#8a97ad", font: { size: 11 } }, grid: { color: "#f1f4f8" } }, x: { ticks: { color: "#8a97ad", font: { size: 11 }, maxRotation: 0, autoSkip: true }, grid: { display: false } } },
      },
    };
  }, [data]);

  const payTotal = (data?.breakdown?.byPayment || []).reduce((s, p) => s + p.revenue, 0) || 1;
  const typeTotal = (data?.breakdown?.byType || []).reduce((s, t) => s + t.revenue, 0) || 1;

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <h4 style={S.title}><i className="fas fa-globe" style={{ color: TEAL }} />Platform Sales — All Vendors</h4>
        <div style={S.toggle} role="tablist" aria-label="Time range">
          {RANGES.map((r) => <button key={r.id} type="button" style={S.tBtn(range === r.id)} onClick={() => setRange(r.id)} aria-pressed={range === r.id}>{r.label}</button>)}
        </div>
      </div>

      {loading && <div style={{ padding: "40px 0", textAlign: "center", color: "#8a97ad" }}>Loading platform sales…</div>}
      {!loading && error && <div className="alert alert-warning mb-0">{error}</div>}

      {!loading && !error && data && (
        <>
          <div style={S.cards}>
            {[{ key: "today", label: "Today", accent: "#f5327a" }, { key: "week", label: "This week", accent: TEAL }, { key: "month", label: "This month", accent: NAVY }].map(({ key, label, accent }) => {
              const s = data.summary?.[key] || { gmv: 0, orders: 0, take: 0 };
              return (
                <div key={key} style={S.card(accent)}>
                  <div style={S.cardLabel}>{label} · GMV</div>
                  <div style={S.cardValue}>{fmtUGX(s.gmv)}</div>
                  <div style={S.cardMeta}>{s.orders} order{s.orders === 1 ? "" : "s"} · Lusuku take {fmtUGX(s.take)}</div>
                </div>
              );
            })}
          </div>

          <div style={S.sectionTitle}>Platform GMV — {RANGES.find((r) => r.id === range)?.sub} (delivered)</div>
          <div style={S.chartBox}><Bar data={chart.data} options={chart.options} /></div>

          <div style={S.sectionTitle}>Vendor leaderboard ({data.totals?.vendorsWithSales || 0} with sales)</div>
          <div style={S.scroll}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>#</th><th style={S.th}>Vendor</th><th style={S.th}>Type</th>
                <th style={{ ...S.th, textAlign: "right" }}>Revenue</th><th style={{ ...S.th, textAlign: "right" }}>Orders</th>
                <th style={{ ...S.th, textAlign: "right" }}>AOV</th><th style={{ ...S.th, textAlign: "right" }}>Fulfil.</th>
                <th style={{ ...S.th, textAlign: "right" }}>Avg del.</th><th style={{ ...S.th, textAlign: "right" }}>Growth</th>
              </tr></thead>
              <tbody>
                {(data.leaderboard || []).length === 0 ? (
                  <tr><td style={S.td} colSpan={9}><span style={S.muted}>No delivered sales in this period yet.</span></td></tr>
                ) : data.leaderboard.map((v, i) => (
                  <tr key={v.vendorId}>
                    <td style={S.td}>{i + 1}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: "#334155", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{v.name}</td>
                    <td style={S.td}><span style={S.pill}>{TYPE_LABEL[v.type] || v.type}</span></td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: "#0f172a" }}>{fmtUGX(v.revenue)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{v.orders}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{fmtUGX(v.aov)}</td>
                    <td style={{ ...S.td, textAlign: "right", color: v.fulfilmentRate < 70 ? "#d64545" : "#64748b" }}>{v.fulfilmentRate}%</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{v.avgDeliveryMinutes ? (v.avgDeliveryMinutes < 60 ? `${v.avgDeliveryMinutes}m` : `${(v.avgDeliveryMinutes / 60).toFixed(1)}h`) : "—"}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{growthTag(v.growthPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <div style={{ ...S.sectionTitle, marginTop: 0 }}>Top movers</div>
              {(data.topMovers || []).length === 0 ? <div style={S.muted}>Not enough history yet.</div> : data.topMovers.map((v) => (
                <div key={v.vendorId} style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{v.name}</span><span>{fmtUGX(v.revenue)} · {growthTag(v.growthPct)}</span></div>
              ))}
            </div>
            <div style={S.panel}>
              <div style={{ ...S.sectionTitle, marginTop: 0 }}>Needs attention</div>
              {(data.atRisk || []).length === 0 ? <div style={S.muted}><i className="fas fa-check-circle" style={{ color: "#0f9d58", marginRight: 6 }} />No vendors need attention</div> : data.atRisk.map((v) => (
                <div key={v.vendorId} style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{v.name}</span><span>{v.fulfilmentRate}% fulfil · {growthTag(v.growthPct)}</span></div>
              ))}
            </div>
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <div style={{ ...S.sectionTitle, marginTop: 0 }}>By vendor type</div>
              {(data.breakdown?.byType || []).map((t) => (
                <div key={t.type} style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{TYPE_LABEL[t.type] || t.type}</span><span style={{ color: "#64748b" }}>{fmtUGX(t.revenue)} · {Math.round((t.revenue / typeTotal) * 100)}%</span></div>
              ))}
            </div>
            <div style={S.panel}>
              <div style={{ ...S.sectionTitle, marginTop: 0 }}>Payment methods</div>
              {(data.breakdown?.byPayment || []).map((p) => (
                <div key={p.method} style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{PAY_LABEL[p.method] || p.method}</span><span style={{ color: "#64748b" }}>{fmtUGX(p.revenue)} · {Math.round((p.revenue / payTotal) * 100)}%</span></div>
              ))}
            </div>
            {(data.breakdown?.byCountry || []).length > 1 && (
              <div style={S.panel}>
                <div style={{ ...S.sectionTitle, marginTop: 0 }}>By country</div>
                {data.breakdown.byCountry.map((c) => (
                  <div key={c.country} style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{c.country}</span><span style={{ color: "#64748b" }}>{fmtUGX(c.revenue)} · {c.orders} orders</span></div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
